import type { BallState, RoundState } from '@bingo/shared';

/**
 * Decide se uma bola revelada é NOVA — a regra de quando cantar.
 *
 * ⚠️ A detecção é por **identidade** (`roundId` + `ordinal`), nunca por
 * quantidade de bolas. O bug que isto corrige: o marcador guardava só o
 * ordinal, e o ordinal **recomeça do 1 a cada rodada**. Numa rodada nova com
 * o painel zerado o painel ia de 30 bolas para 1, e a primeira bola ou era
 * adotada em silêncio (telão) ou ficava 30 bolas sem ser cantada (controle,
 * que nem reiniciava o marcador). A segunda em diante voltava a funcionar —
 * o que fazia o defeito parecer aleatório.
 *
 * Três regras, nesta ordem:
 *
 * 1. **Sem marcador = primeira carga.** O telão que liga no meio do jogo
 *    adota o estado em silêncio: ninguém quer ouvir as 30 bolas que já
 *    saíram.
 * 2. **Bola herdada nunca é cantada.** Ela já foi cantada na rodada em que
 *    saiu de verdade; é cópia, não bola nova.
 * 3. **Rodada diferente = bola nova.** Comparar ordinais entre rodadas não
 *    significa nada, porque a numeração reinicia. Dentro da mesma rodada,
 *    vale o ordinal maior.
 */

export interface NarrationMark {
  roundId: string;
  /** Último ordinal observado nesta rodada. `0` = nenhum ainda. */
  ordinal: number;
}

/**
 * Marcador para uma rodada que acabou de começar.
 *
 * Painel zerado devolve `ordinal: 0` — e isso é o ponto. Devolver `null`
 * (ou não reiniciar nada) é exatamente o que engolia a primeira bola.
 */
export function markForRound(round: Pick<RoundState, 'id' | 'balls'>): NarrationMark {
  return { roundId: round.id, ordinal: round.balls.at(-1)?.ordinal ?? 0 };
}

export function shouldNarrateBall(
  mark: NarrationMark | null,
  roundId: string,
  ball: BallState,
): boolean {
  if (!mark) return false;
  if (ball.inherited) return false;
  if (mark.roundId !== roundId) return true;

  return ball.ordinal > mark.ordinal;
}

/** Avança o marcador para a bola observada — cantada ou não. */
export function advanceMark(roundId: string, ball: BallState): NarrationMark {
  return { roundId, ordinal: ball.ordinal };
}
